import React from 'react'
import { Form, Input, Button, message } from 'antd'
import { FiImage } from 'react-icons/fi'
import { useTranslation } from 'react-i18next'
import AspectRatioSelector from './AspectRatioSelector'
import { ImagePreview } from '../../../../../../../../../components'

const AiImagesForm = ({
    form,
    getFieldDecorator,
    promptText,
    isImageBeingGenerated,
    handleGenerateImage,
    setPromptText,
    promptTitle,
    setPromptTitle,
    generationStatus,
    includeText,
    refImages,
    setRefImages,
    showUploader,
    showPromptHelper,
    promptHelperNode,
    isUploading,
    onOpenPixelSearch,
    setShowMediaLibrary,
    mediaLoading,
    activeTab,
    isCover
}) => {
    const handleAddImages = async files => {
        const fileArr = Array.from(files || [])
        if (!fileArr.length) return

        const remaining = Math.max(0, 9 - refImages.length)
        const toAdd = fileArr.slice(0, remaining)

        const maxSizeMB = 5
        const validFiles = toAdd.filter(file => {
            const sizeMB = file.size / (1024 * 1024)
            if (sizeMB > maxSizeMB) {
                message.error(
                    `${t('v5:file')} "${file.name}" ${t(
                        'v5:is_too_large'
                    )} (${sizeMB.toFixed(2)} ${t('v5:mb')}). ${t(
                        'v5:limit_is'
                    )} ${maxSizeMB} ${t('v5:mb')}.`
                )
                return false
            }
            return true
        })

        if (!validFiles.length) return

        const readers = validFiles.map(
            file =>
                new Promise(resolve => {
                    const reader = new FileReader()
                    reader.onload = () => {
                        const result = reader.result
                        if (typeof result === 'string') {
                            resolve({
                                id: Date.now() + Math.random(),
                                preview: result,
                                base64: result.split(',')[1] || '',
                                mime: file.type || 'image/png',
                                sizeMB: (file.size / (1024 * 1024)).toFixed(2)
                            })
                        } else {
                            resolve(null)
                        }
                    }
                    reader.readAsDataURL(file)
                })
        )

        const results = (await Promise.all(readers)).filter(Boolean)
        const newRefImages = [...refImages, ...results]
        setRefImages(newRefImages)

        // Clear validation errors when images are added
        form.setFields({
            referenceImages: {
                value: newRefImages.length,
                errors: null
            }
        })
    }

    const handleRemove = id => {
        const newRefImages = refImages.filter(img => img.id !== id)
        setRefImages(newRefImages)

        // Trigger validation after removing images
        setTimeout(() => {
            if (activeTab === 'image-edit') {
                form.validateFields(['referenceImages'], () => { })
            }
        }, 100)
    }
    const { t } = useTranslation()

    return (
        <Form
            className="ai-images-form"
            onSubmit={e => {
                e.preventDefault()
                handleGenerateImage()
            }}
            layout="vertical"
        >
            <div id="promptInput" />
            {!showPromptHelper && (
                <Form.Item name="prompt">
                    {getFieldDecorator('prompt', {
                        initialValue: promptText,
                        rules: [
                            {
                                required: true,
                                message: t('v5:please_enter_image_idea')
                            }
                        ]
                    })(
                        <Input.TextArea
                            disabled={isImageBeingGenerated}
                            placeholder={t('v5:your_prompt_here')}
                            autoSize={{ minRows: 2, maxRows: 5 }}
                            onChange={e => setPromptText(e.target.value)}
                        />
                    )}
                </Form.Item>
            )}

            {promptHelperNode}
            {!isCover && <AspectRatioSelector form={form} disabled={isImageBeingGenerated} />}
            {showUploader && (
                <>
                    <div
                        className="nbai-reference-header"
                        style={{
                            marginBottom: '8px',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center'
                        }}
                    >
                        <div>
                            <span>
                                {t('v4:reference_image')}{' '}
                                <span style={{ color: 'red' }}>*</span> {refImages.length}/9
                            </span>
                        </div>
                        <div className="nbai-btn-wrap">
                            <Button
                                size="small"
                                type="link"
                                className="nbai-link-btn"
                                onClick={setShowMediaLibrary}
                                loading={mediaLoading}
                            >
                                <FiImage />
                                {t('buttons:select_media_library')}
                            </Button>
                            <Button
                                size="small"
                                type="link"
                                className="nbai-link-btn"
                                onClick={onOpenPixelSearch}
                                disabled={mediaLoading}
                            >
                                <FiImage />
                                {t('v4:select_pexels_image')}
                            </Button>
                        </div>
                    </div>
                    <Form.Item
                        name="referenceImages"
                        rules={[
                            {
                                validator: (rule, value, callback) => {
                                    if (refImages.length === 0) {
                                        callback(t('v5:please_upload_reference_image'))
                                    } else {
                                        callback()
                                    }
                                }
                            }
                        ]}
                    >
                        <input type="hidden" value={refImages.length} />
                        <div className="nbai-grid">
                            {refImages.map(img => (
                                <div className="nbai-tile" key={img.id}>
                                    <ImagePreview
                                        src={img.preview}
                                        alt={t('v5:reference_image_alt')}
                                    />
                                    <div className="nbai-size">{img.sizeMB} MB</div>
                                    <button
                                        type="button"
                                        className="nbai-remove"
                                        onClick={() => handleRemove(img.id)}
                                    >
                                        ×
                                    </button>
                                </div>
                            ))}
                            {refImages.length < 9 && (
                                <label className="nbai-tile nbai-tile--add">
                                    <div className="nbai-plus">+</div>
                                    <div className="nbai-add-meta">
                                        {t('v4:add_image')}
                                        <br />
                                        {t('v4:max_mb')}
                                    </div>
                                    <input
                                        type="file"
                                        accept="image/*"
                                        multiple
                                        style={{ display: 'none' }}
                                        disabled={isImageBeingGenerated}
                                        onChange={e => handleAddImages(e.target.files)}
                                    />
                                </label>
                            )}
                        </div>
                    </Form.Item>
                </>
            )}
            {includeText && showUploader && (
                <Form.Item name="title" label={t('v5:icon_text')}>
                    {getFieldDecorator('title', {
                        initialValue: promptTitle,
                        rules: [
                            {
                                required: false,
                                message: t('v4:please_enter_text_to_appear_image')
                            }
                        ]
                    })(
                        <Input
                            disabled={isImageBeingGenerated}
                            size="large"
                            placeholder={t('v4:enter_text_to_apear_in_the_image')}
                            onChange={e => setPromptTitle(e.target.value)}
                        />
                    )}
                </Form.Item>
            )}

            <Form.Item>
                <Button
                    onClick={() => handleGenerateImage()}
                    disabled={
                        (isImageBeingGenerated && generationStatus.status !== 'error') ||
                        isUploading ||
                        mediaLoading
                    }
                    type="primary"
                    size="large"
                    loading={isImageBeingGenerated}
                >
                    {t('v4:generate_image')}
                </Button>
            </Form.Item>
        </Form>
    )
}

export default AiImagesForm
